/**
 * Ledger Summary Component - Displays financial summary cards
 */

import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  }).format(amount || 0);
};

const LedgerSummary = ({ summary, loading, error, type = "job" }) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!summary) {
    return null;
  }

  if (type === "customer") {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Charges
              </Typography>
              <Typography variant="h5">
                {formatCurrency(summary.totalCharges || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Payments
              </Typography>
              <Typography variant="h5" color="success.main">
                {formatCurrency(summary.totalPayments || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Refunds
              </Typography>
              <Typography variant="h5" color="error.main">
                {formatCurrency(summary.totalRefunds || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Outstanding Balance
              </Typography>
              <Typography
                variant="h5"
                color={summary.netBalance > 0 ? "error.main" : "success.main"}
              >
                {formatCurrency(summary.netBalance || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  // Job summary
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Revenue
            </Typography>
            <Typography variant="h5" color="success.main">
              {formatCurrency(summary.totalRevenue || 0)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Payouts
            </Typography>
            <Typography variant="h5" color="error.main">
              {formatCurrency(summary.totalPayouts || 0)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography color="text.secondary" gutterBottom sx={{ mb: 0 }}>
                Net Customer Paid
              </Typography>
              <Tooltip
                title="Net Customer Paid = Total Revenue − Refunds. This represents actual customer payments after refunds, but does not subtract payouts to providers."
                arrow
              >
                <IconButton size="small" sx={{ p: 0, ml: 0.5 }}>
                  <InfoOutlined fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="h5">
              {formatCurrency(summary.netRevenue || 0)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Platform Earnings
            </Typography>
            <Typography variant="h5" color="primary.main">
              {formatCurrency(summary.totalPlatformFees || 0)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      {summary.margin !== null && summary.margin !== undefined && (
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Margin
              </Typography>
              <Typography variant="h5">
                {`${(summary.margin * 100).toFixed(1)}%`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default LedgerSummary;
